import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { PageSectionCard } from "../../components/layout";
import AppAutocomplete from "../../components/form/AppAutocomplete";
import AppTextField from "../../components/form/AppTextField";
import AppButton from "../../components/form/AppButton";
import {
  dataIsolationService,
  type DataIsolationRule,
  type IsolationValueSource,
} from "../../services/dataIsolationService";

const VALUE_SOURCES: IsolationValueSource[] = ["ISOLATION_VALUE", "EXTERNAL_USER_ID", "EMAIL"];
const FIELD_PATTERN = /^[A-Za-z_][A-Za-z0-9_.]*$/;
const COLUMN_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * Workspace admin screen for data isolation: the field that says which of our own end users a row or
 * document belongs to. With it on, a chatbot user only ever gets their own data — the backend adds the
 * filter to every query and refuses any query it cannot narrow.
 */
export default function DataIsolationCard() {
  const { t } = useTranslation();
  const [rule, setRule] = useState<DataIsolationRule | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [field, setField] = useState("");
  const [adaptorField, setAdaptorField] = useState("");
  const [valueSource, setValueSource] = useState<IsolationValueSource>("ISOLATION_VALUE");
  const [applyToOpenSearch, setApplyToOpenSearch] = useState(true);
  const [applyToAdaptors, setApplyToAdaptors] = useState(true);
  const [description, setDescription] = useState("");

  const apply = (data: DataIsolationRule) => {
    setRule(data);
    setEnabled(data.enabled);
    setField(data.field || "");
    setAdaptorField(data.adaptorField || "");
    setValueSource(data.valueSource || "ISOLATION_VALUE");
    setApplyToOpenSearch(data.applyToOpenSearch);
    setApplyToAdaptors(data.applyToAdaptors);
    setDescription(data.description || "");
  };

  useEffect(() => {
    let cancelled = false;
    dataIsolationService
      .get()
      .then((data) => !cancelled && apply(data))
      .catch(() => !cancelled && setLoadError(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const fieldInvalid = field.trim().length > 0 && !FIELD_PATTERN.test(field.trim());
  const adaptorFieldInvalid = adaptorField.trim().length > 0 && !COLUMN_PATTERN.test(adaptorField.trim());
  const needsField = enabled && field.trim().length === 0;
  const needsTarget = enabled && !applyToOpenSearch && !applyToAdaptors;
  const canSave = !saving && !fieldInvalid && !adaptorFieldInvalid && !needsField && !needsTarget;

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const data = await dataIsolationService.update({
        enabled,
        field: field.trim() || null,
        adaptorField: adaptorField.trim() || null,
        valueSource,
        applyToOpenSearch,
        applyToAdaptors,
        description: description.trim() || null,
      });
      apply(data);
      setSaved(true);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : t("dataIsolation.saveError"));
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <PageSectionCard title={t("dataIsolation.title")} description={t("dataIsolation.description")}>
        <Alert severity="error">{t("dataIsolation.loadError")}</Alert>
      </PageSectionCard>
    );
  }
  if (!rule) {
    return (
      <PageSectionCard title={t("dataIsolation.title")} description={t("dataIsolation.description")}>
        <CircularProgress size={24} />
      </PageSectionCard>
    );
  }

  return (
    <PageSectionCard title={t("dataIsolation.title")} description={t("dataIsolation.description")}>
      <Stack spacing={2}>
        <FormControlLabel
          control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
          label={t("dataIsolation.enabled")}
        />

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
          <AppTextField
            label={t("dataIsolation.field")}
            value={field}
            onChange={(e) => setField(e.target.value)}
            error={fieldInvalid || needsField}
            helperText={
              fieldInvalid
                ? t("dataIsolation.fieldInvalid")
                : needsField
                  ? t("dataIsolation.fieldRequired")
                  : t("dataIsolation.fieldHelp")
            }
            placeholder="ownerUserId"
          />
          <AppTextField
            label={t("dataIsolation.adaptorField")}
            value={adaptorField}
            onChange={(e) => setAdaptorField(e.target.value)}
            error={adaptorFieldInvalid}
            helperText={adaptorFieldInvalid ? t("dataIsolation.adaptorFieldInvalid") : t("dataIsolation.adaptorFieldHelp")}
            placeholder="owner_user_id"
          />
        </Box>

        <AppAutocomplete
          label={t("dataIsolation.valueSource")}
          options={VALUE_SOURCES}
          value={valueSource}
          disableClearable
          getOptionLabel={(source) => t(`dataIsolation.valueSources.${source}`)}
          onChange={(_, value) => value && setValueSource(value)}
        />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <FormControlLabel
            control={<Switch checked={applyToOpenSearch} onChange={(e) => setApplyToOpenSearch(e.target.checked)} />}
            label={t("dataIsolation.applyToOpenSearch")}
          />
          <FormControlLabel
            control={<Switch checked={applyToAdaptors} onChange={(e) => setApplyToAdaptors(e.target.checked)} />}
            label={t("dataIsolation.applyToAdaptors")}
          />
        </Stack>
        {needsTarget && <Alert severity="warning">{t("dataIsolation.targetRequired")}</Alert>}

        <AppTextField
          label={t("dataIsolation.notes")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={2}
        />

        <Typography variant="body2" color="text.secondary">
          {t("dataIsolation.endUserCount", { count: rule.endUserCount })}
        </Typography>
        {rule.endUsersWithoutValue > 0 && (
          <Alert severity={enabled ? "warning" : "info"}>
            {t("dataIsolation.missingValues", { count: rule.endUsersWithoutValue })}
          </Alert>
        )}

        {saveError && <Alert severity="error">{saveError}</Alert>}
        {saved && <Alert severity="success">{t("dataIsolation.saved")}</Alert>}

        <Box>
          <AppButton onClick={() => void save()} disabled={!canSave} loading={saving}>
            {t("common.save")}
          </AppButton>
        </Box>
      </Stack>
    </PageSectionCard>
  );
}
